import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock, Search, Tag } from "lucide-react";
import { BLOG_POSTS } from "@/lib/blog-data";
import useSEO from "@/hooks/useSEO";

function formatDate(d) {
  return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export default function Blog() {
  useSEO({
    title: "Kothari Classes Blog | Study Tips, JEE & NEET Guidance in Jalgaon",
    description: "Latest study tips, career guidance and exam strategies from Kothari Classes — the most trusted JEE, NEET, MH-CET and Foundation coaching institute in Jalgaon.",
    canonical: "https://www.kothariclasses.in/blog",
  });

  const categories = useMemo(() => ["All", ...Array.from(new Set(BLOG_POSTS.map((p) => p.category)))], []);
  const [active, setActive] = useState("All");
  const [q, setQ] = useState("");

  const filtered = BLOG_POSTS.filter((p) => {
    const matchesCat = active === "All" || p.category === active;
    const matchesQ = !q || (p.title + p.excerpt).toLowerCase().includes(q.toLowerCase());
    return matchesCat && matchesQ;
  });

  const [featured, ...rest] = filtered;

  return (
    <div data-testid="blog-page">
      {/* Header */}
      <section className="bg-gray-950 text-white py-20 lg:py-24 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-[0.04]" style={{backgroundImage:"radial-gradient(circle at 2px 2px, #fff 1px, transparent 1px)", backgroundSize:"32px 32px"}}/>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <span className="text-xs font-bold tracking-[0.22em] text-red-500 uppercase">Insights · Guides</span>
            <h1 className="font-display mt-3 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1]">
              Kothari Classes Blog
            </h1>
            <p className="mt-5 text-gray-400 text-lg max-w-xl">
              Latest Study Tips, Career Guidance &amp; Exam Strategies — from our Jalgaon faculty, written for real students and parents.
            </p>
          </div>
        </div>
      </section>

      {/* Filter bar */}
      <section className="bg-white border-b border-gray-200 sticky top-16 lg:top-20 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col md:flex-row gap-4 md:items-center justify-between">
          <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="blog-categories">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                data-testid={`blog-category-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`whitespace-nowrap text-xs font-bold uppercase tracking-widest px-4 py-2 border transition-colors ${
                  active === c ? "bg-gray-950 text-white border-gray-950" : "bg-white text-gray-700 border-gray-300 hover:border-red-600 hover:text-red-600"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="relative w-full md:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search articles..."
              data-testid="blog-search-input"
              className="w-full border border-gray-300 pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:border-red-600 focus:ring-1 focus:ring-red-600 rounded-none"
            />
          </div>
        </div>
      </section>

      {/* Featured */}
      {featured && (
        <section className="bg-white py-14" data-testid="blog-featured-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link
              to={`/blog/${featured.slug}`}
              data-testid={`blog-featured-${featured.slug}`}
              className="group grid lg:grid-cols-12 gap-8 border border-gray-200 card-lift"
            >
              <div className="lg:col-span-7 relative overflow-hidden">
                <img src={featured.image} alt={featured.title} className="w-full h-64 lg:h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute top-4 left-4 bg-red-600 text-white text-[10px] font-black uppercase tracking-widest px-2.5 py-1">Featured</div>
              </div>
              <div className="lg:col-span-5 p-8 lg:p-10 flex flex-col">
                <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-gray-500">
                  <Tag className="w-3.5 h-3.5 text-red-600" /> {featured.category}
                </div>
                <h2 className="font-display text-3xl lg:text-4xl font-black tracking-tight text-gray-950 mt-4 leading-tight group-hover:text-red-600 transition-colors">
                  {featured.title}
                </h2>
                <p className="text-gray-600 mt-4 leading-relaxed">{featured.excerpt}</p>
                <div className="flex items-center gap-5 text-xs text-gray-500 mt-6">
                  <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{formatDate(featured.date)}</span>
                  <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{featured.read}</span>
                </div>
                <span className="inline-flex items-center gap-2 font-semibold text-red-600 mt-8 text-sm group-hover:translate-x-1 transition-transform">
                  Read More <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="bg-gray-50 py-16 lg:py-20" data-testid="blog-grid-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-8">
            <div>
              <span className="eyebrow">All Articles</span>
              <h2 className="font-display text-2xl sm:text-3xl font-black text-gray-950 mt-2">
                {filtered.length} {filtered.length === 1 ? "article" : "articles"}
              </h2>
            </div>
          </div>

          {rest.length === 0 && filtered.length <= 1 && (
            <p className="text-gray-500 text-sm">No more articles in this category.</p>
          )}

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {rest.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="group bg-white border border-gray-200 card-lift flex flex-col overflow-hidden"
              >
                <div className="relative overflow-hidden h-52">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute top-3 left-3 bg-white text-red-600 text-[10px] font-black uppercase tracking-widest px-2.5 py-1 border border-red-200">
                    {p.category}
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="font-display text-xl font-bold text-gray-950 leading-snug group-hover:text-red-600 transition-colors">
                    {p.title}
                  </h3>
                  <p className="text-sm text-gray-600 mt-3 leading-relaxed flex-1 line-clamp-3">{p.excerpt}</p>
                  <div className="flex items-center justify-between mt-5 pt-4 border-t border-gray-200 text-xs text-gray-500">
                    <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{formatDate(p.date)}</span>
                    <span className="font-semibold text-red-600 flex items-center gap-1 group-hover:gap-2 transition-all">
                      Read More <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
